if (!customElements.get('byte-quantity-limit')) {
  const CART_MUTATION_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const CART_SYNC_DELAY = 150;

  class ByteQuantityLimit extends HTMLElement {
    static #cartRequest = null;

    constructor() {
      super();

      this.maxQuantity = 0;
      this.cartProductQuantity = 0;
      this.lineItemKey = null;
      this.pendingIncrementFrom = null;
      this.cartObserver = null;
      this.syncTimeoutId = null;
      this.isProductContext = false;
      this.isSubmitValidated = false;
      this.isRecheckedClick = false;
      this.isCheckingCart = false;
      this.stateFrameId = null;

      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleInputChange = this.handleInputChange.bind(this);
      this.boundHandleQuantityUpdate = this.handleQuantityUpdate.bind(this);
      this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
      this.boundHandlePageShow = this.handlePageShow.bind(this);
      this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.boundHandleCartRequests = this.handleCartRequests.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      if (!this.quantityInput) return;

      this.maxQuantity = Number(this.dataset.quantityLimitMax) || 0;

      this.#bindEvents();
      this.#observeCartRequests();
      this.#updatePlusButtonState();
      this.syncMaxQuantity();
    }

    disconnectedCallback() {
      this.#unbindEvents();
      this.#disconnectCartObserver();

      if (this.stateFrameId) {
        cancelAnimationFrame(this.stateFrameId);
      }

      if (this.syncTimeoutId) {
        clearTimeout(this.syncTimeoutId);
      }
    }

    handleClick(event) {
      const plusButton = event.target.closest('[data-add-quantity]');
      if (!plusButton) return;

      const currentQuantity = Number(this.quantityInput.value) || 0;

      // Click re-dispatched after a successful live cart check: let the theme handle it
      if (this.isRecheckedClick) {
        this.isRecheckedClick = false;
        this.#trackIncrement(currentQuantity);
        return;
      }

      if (currentQuantity < this.maxQuantity) {
        this.#trackIncrement(currentQuantity);
        return;
      }

      // Capture phase: stops the theme's button listener (PDP) and cart Delegate (cart/drawer)
      event.preventDefault();
      event.stopImmediatePropagation();
      this.recheckPlusClick(plusButton);
    }

    handleInputChange(event) {
      if (event.target !== this.quantityInput) return;

      const requestedQuantity = Number(this.quantityInput.value) || 0;

      if (requestedQuantity > this.maxQuantity) {
        this.quantityInput.value = String(Math.max(this.maxQuantity, 1));
        this.#showLimitMessage();
      }
    }

    handleQuantityUpdate() {
      // Runs after the theme has had a chance to change the value
      if (this.stateFrameId) {
        cancelAnimationFrame(this.stateFrameId);
      }

      this.stateFrameId = requestAnimationFrame(() => {
        this.stateFrameId = null;
        this.#applyMissedIncrement();
        this.#updatePlusButtonState();
      });
    }

    handleFormSubmit(event) {
      if (event.target !== this.productForm) return;

      // Second pass after successful validation: let the theme's product form handle it
      if (this.isSubmitValidated) {
        this.isSubmitValidated = false;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.isCheckingCart) return;

      this.validateAddToCart(event.submitter);
    }

    handlePageShow(event) {
      // Page restored from back/forward cache: cart may have changed meanwhile
      if (!event.persisted) return;

      this.syncMaxQuantity();
    }

    handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return;

      this.syncMaxQuantity();
    }

    handleCartRequests(entryList) {
      // Any finished cart mutation (theme, drawer, apps) triggers a live resync
      for (const entry of entryList.getEntries()) {
        if (!CART_MUTATION_PATTERN.test(entry.name)) continue;

        this.#scheduleSync();
        return;
      }
    }

    async syncMaxQuantity() {
      try {
        const cartState = await this.#fetchCartState();
        this.#applyCartState(cartState);
      } catch (error) {
        // Keep the current limit if cart.js is unavailable
      }
    }

    async recheckPlusClick(plusButton) {
      if (this.isCheckingCart) return;

      this.#setLoadingState(true);

      try {
        const cartState = await this.#fetchCartState();
        this.#applyCartState(cartState);

        const currentQuantity = Number(this.quantityInput.value) || 0;

        if (currentQuantity < this.maxQuantity) {
          this.isRecheckedClick = true;
          plusButton.click();
          return;
        }

        this.#showLimitMessage();
      } catch (error) {
        this.#showLimitMessage();
      } finally {
        this.#setLoadingState(false);
      }
    }

    async validateAddToCart(submitter) {
      this.#setLoadingState(true);

      try {
        const cartState = await this.#fetchCartState();
        const requestedQuantity = Number(this.quantityInput.value) || 1;

        this.#applyCartState(cartState);

        if (requestedQuantity > this.maxQuantity) {
          this.#showLimitMessage();
          return;
        }

        this.#resubmitForm(submitter);
      } catch (error) {
        // Fail open: a cart.js network error should not block purchases
        this.#resubmitForm(submitter);
      } finally {
        this.#setLoadingState(false);
      }
    }

    async #fetchCartState() {
      const cart = await ByteQuantityLimit.#fetchCart();
      const productId = Number(this.dataset.quantityLimitProductId);
      let productQuantity = 0;
      let lineQuantity = 0;

      for (const cartItem of cart.items) {
        if (cartItem.product_id !== productId) continue;

        productQuantity += cartItem.quantity;

        if (this.lineItemKey && cartItem.key === this.lineItemKey) {
          lineQuantity = cartItem.quantity;
        }
      }

      return { productQuantity, lineQuantity };
    }

    static #fetchCart() {
      // Share one in-flight request between all limited inputs on the page
      if (!ByteQuantityLimit.#cartRequest) {
        const cartRoot = window.Shopify?.routes?.root ?? '/';

        ByteQuantityLimit.#cartRequest = fetch(`${cartRoot}cart.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Cart fetch failed: ${response.status}`);
            }

            return response.json();
          })
          .finally(() => {
            ByteQuantityLimit.#cartRequest = null;
          });
      }

      return ByteQuantityLimit.#cartRequest;
    }

    #cacheElements() {
      this.quantityInput = this.querySelector('[data-quantity-input]');
      this.plusButton = this.querySelector('[data-add-quantity]');
      this.statusMessage = this.querySelector('[data-quantity-limit-status]');
      this.addToCartButtons = [];

      if (!this.quantityInput) return;

      // Cart page and quick cart wrap each line in [data-input-item data-key="..."]
      const lineItem = this.closest('[data-input-item]');
      this.lineItemKey = lineItem?.dataset.key ?? null;
      this.isProductContext = !this.lineItemKey;

      if (!this.isProductContext) {
        this.productForm = null;
        return;
      }

      // PDP: sticky bar sits inside the form; main block input may sit outside it
      const productContainer = this.closest('[data-is-quick-view], [data-section-type]');

      this.productForm =
        this.closest('[data-product-form]') ??
        productContainer?.querySelector('[data-product-form]') ??
        productContainer?.querySelector('form[action*="/cart/add"]') ??
        null;

      // Main buy button and sticky bar button both carry data-add-to-cart
      const buttonScope = productContainer ?? this.productForm;
      this.addToCartButtons = buttonScope
        ? [...buttonScope.querySelectorAll('[data-add-to-cart]')]
        : [];
    }

    #bindEvents() {
      // Capture phase so the guard runs before the theme's quantity listeners
      this.addEventListener('click', this.boundHandleClick, true);
      this.addEventListener('change', this.boundHandleInputChange, true);

      // Refresh button state after the theme updates the value
      this.addEventListener('click', this.boundHandleQuantityUpdate);
      this.addEventListener('input', this.boundHandleQuantityUpdate);
      this.addEventListener('change', this.boundHandleQuantityUpdate, true);

      window.addEventListener('pageshow', this.boundHandlePageShow);
      document.addEventListener('visibilitychange', this.boundHandleVisibilityChange);

      if (this.productForm) {
        document.addEventListener('submit', this.boundHandleFormSubmit, true);
      }
    }

    #observeCartRequests() {
      if (!('PerformanceObserver' in window)) return;

      try {
        this.cartObserver = new PerformanceObserver(this.boundHandleCartRequests);
        this.cartObserver.observe({ type: 'resource', buffered: false });
      } catch (error) {
        this.cartObserver = null;
      }
    }

    #scheduleSync() {
      if (this.syncTimeoutId) {
        clearTimeout(this.syncTimeoutId);
      }

      this.syncTimeoutId = setTimeout(() => {
        this.syncTimeoutId = null;
        this.syncMaxQuantity();
      }, CART_SYNC_DELAY);
    }

    #trackIncrement(currentQuantity) {
      // PDP only: cart lines update through the theme's /cart/change flow
      if (!this.isProductContext) return;

      this.pendingIncrementFrom = currentQuantity;
    }

    #applyMissedIncrement() {
      if (this.pendingIncrementFrom === null) return;

      const previousQuantity = this.pendingIncrementFrom;
      this.pendingIncrementFrom = null;

      const currentQuantity = Number(this.quantityInput.value) || 0;
      if (currentQuantity !== previousQuantity) return;

      // Theme did not handle this plus button (e.g. sticky bar): increment ourselves
      const nextQuantity = Math.min(previousQuantity + 1, this.maxQuantity);
      if (nextQuantity <= previousQuantity) return;

      this.quantityInput.value = String(nextQuantity);
      this.quantityInput.dispatchEvent(new Event('change'));
    }

    #applyCartState({ productQuantity, lineQuantity }) {
      const quantityLimit = Number(this.dataset.quantityLimit) || 0;

      // Cart lines must not count their own quantity against themselves
      const ownLineQuantity = this.isProductContext ? 0 : lineQuantity;
      const otherCartQuantity = Math.max(productQuantity - ownLineQuantity, 0);

      this.cartProductQuantity = productQuantity;
      this.maxQuantity = Math.max(quantityLimit - otherCartQuantity, 0);

      // PDP: keep the selected quantity inside the remaining allowance
      if (this.isProductContext) {
        const currentQuantity = Number(this.quantityInput.value) || 1;
        const clampedQuantity = Math.max(Math.min(currentQuantity, this.maxQuantity), 1);

        if (clampedQuantity !== currentQuantity) {
          this.quantityInput.value = String(clampedQuantity);
          this.quantityInput.dispatchEvent(new Event('change'));
        }
      }

      this.#updatePlusButtonState();
      this.#updateAddToCartState();
    }

    #resubmitForm(submitter) {
      if (!this.productForm) return;

      this.isSubmitValidated = true;

      if (submitter && submitter.form === this.productForm) {
        this.productForm.requestSubmit(submitter);
      } else {
        this.productForm.requestSubmit();
      }
    }

    #updatePlusButtonState() {
      if (!this.plusButton) return;

      this.quantityInput.max = String(Math.max(this.maxQuantity, 1));

      const currentQuantity = Number(this.quantityInput.value) || 0;
      const isLimitReached = currentQuantity >= this.maxQuantity;

      // Clean up legacy inline styles / disabled attribute from the old limit script
      this.plusButton.disabled = false;
      this.plusButton.style.removeProperty('pointer-events');
      this.plusButton.style.removeProperty('opacity');

      this.plusButton.setAttribute('aria-disabled', String(isLimitReached));
      this.plusButton.classList.toggle('is-disabled', isLimitReached);

      this.#renderCartInfo();
    }

    #updateAddToCartState() {
      if (!this.isProductContext) return;

      const isLimitFull = this.maxQuantity === 0;

      for (const addToCartButton of this.addToCartButtons) {
        const isDisabledByLimit = addToCartButton.hasAttribute('data-quantity-limit-disabled');

        if (isLimitFull && !addToCartButton.disabled) {
          // Only mark buttons we disable, so sold-out buttons are never re-enabled by us
          addToCartButton.disabled = true;
          addToCartButton.setAttribute('aria-disabled', 'true');
          addToCartButton.setAttribute('data-quantity-limit-disabled', '');
        }

        if (!isLimitFull && isDisabledByLimit) {
          addToCartButton.disabled = false;
          addToCartButton.removeAttribute('aria-disabled');
          addToCartButton.removeAttribute('data-quantity-limit-disabled');
        }
      }
    }

    #renderCartInfo() {
      if (!this.statusMessage) return;

      const hasProductInCart = this.isProductContext && this.cartProductQuantity > 0;

      if (!hasProductInCart) {
        this.#hideStatus();
        return;
      }

      // Always explain the cap on PDP so a locked plus button never looks like a bug
      if (this.maxQuantity === 0) {
        this.#showStatus(this.dataset.quantityLimitMessage ?? '', true);
        return;
      }

      this.#showStatus(this.#getRemainingMessage(), false);
    }

    #showLimitMessage() {
      const hasRemainingInfo = this.isProductContext && this.cartProductQuantity > 0 && this.maxQuantity > 0;
      const limitMessage = hasRemainingInfo
        ? this.#getRemainingMessage()
        : this.dataset.quantityLimitMessage ?? '';

      this.#showStatus(limitMessage, true);
    }

    #getRemainingMessage() {
      return (this.dataset.quantityLimitRemainingMessage ?? '')
        .replace('[in_cart]', String(this.cartProductQuantity))
        .replace('[remaining]', String(this.maxQuantity));
    }

    #showStatus(message, hasError) {
      if (!this.statusMessage || !message) return;

      this.statusMessage.textContent = message;
      this.statusMessage.classList.toggle('has-error', hasError);
      this.statusMessage.hidden = false;
    }

    #hideStatus() {
      if (!this.statusMessage) return;

      this.statusMessage.hidden = true;
      this.statusMessage.textContent = '';
      this.statusMessage.classList.remove('has-error');
    }

    #setLoadingState(isLoading) {
      this.isCheckingCart = isLoading;
      this.classList.toggle('is-loading', isLoading);
    }

    #disconnectCartObserver() {
      this.cartObserver?.disconnect();
      this.cartObserver = null;
    }

    #unbindEvents() {
      this.removeEventListener('click', this.boundHandleClick, true);
      this.removeEventListener('change', this.boundHandleInputChange, true);
      this.removeEventListener('click', this.boundHandleQuantityUpdate);
      this.removeEventListener('input', this.boundHandleQuantityUpdate);
      this.removeEventListener('change', this.boundHandleQuantityUpdate, true);
      window.removeEventListener('pageshow', this.boundHandlePageShow);
      document.removeEventListener('visibilitychange', this.boundHandleVisibilityChange);
      document.removeEventListener('submit', this.boundHandleFormSubmit, true);
    }
  }

  customElements.define('byte-quantity-limit', ByteQuantityLimit);
}